import { IssuePriority, IssueStatus, type IIssue, type IIssueAssignee } from '../../trusstor/index.js';
import type { IssueLocation } from './issueLocation.js';

export type FormState = {
  description: string;
  priority: IssuePriority;
  status: IssueStatus;
  profession: IIssue['profession'] | null;
  assignee: IIssueAssignee | null;
  locations: IssueLocation[];
  dueDate: Date | null;
  isSafety: boolean;
  isPrivate: boolean;
  /** The D-ORCA task the issue is raised against. */
  taskId: string | null;
};

const DEFAULTS: FormState = {
  description: '',
  priority: IssuePriority.REGULAR,
  status: IssueStatus.PLANNED,
  profession: null,
  assignee: null,
  locations: [],
  dueDate: null,
  isSafety: false,
  isPrivate: false,
  taskId: null,
};

export type FormAction =
  | { type: 'set'; field: keyof FormState; value: FormState[keyof FormState] }
  | { type: 'reset'; state: FormState };

export function createInitialFormState(opts: {
  mode: 'create' | 'edit';
  initial: Partial<FormState> | null;
}): FormState {
  const merged = { ...DEFAULTS, ...(opts.initial ?? {}) };
  // Trusstor returns dueDate as an ISO string, but the form contract wants a
  // real Date. Coerce here so downstream consumers (DueDateField) don't crash
  // on `Intl.formatToParts(<string>)`.
  return { ...merged, dueDate: toValidDate(merged.dueDate) };
}

function toValidDate(v: FormState['dueDate']): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v as unknown as string);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'set': {
      const next = { ...state, [action.field]: action.value };
      // A safety issue acts in Trusstor's safety engine through its area, so it
      // can't outlive its last location (cleared only when the locations change,
      // so editing another field never rewrites a stored flag).
      return action.field === 'locations' && next.locations.length === 0 ? { ...next, isSafety: false } : next;
    }
    case 'reset':
      return action.state;
  }
}

/** The create body for a filled-in form. Unset optional fields are left out
 *  rather than sent as null. */
export function toCreateBody(state: FormState): Partial<IIssue> {
  return {
    description: state.description,
    priority: state.priority,
    status: state.status,
    profession: state.profession ?? undefined,
    assignee: state.assignee,
    locations: state.locations,
    dueDate: state.dueDate ?? undefined,
    isSafety: state.isSafety,
    isPrivate: state.isPrivate,
    taskId: state.taskId ?? undefined,
  };
}

/** Returns only the keys that differ between `prev` and `next` (shallow compare). */
export function dirtyDiff(prev: FormState, next: FormState): Partial<FormState> {
  const out: Partial<FormState> = {};
  const keys: (keyof FormState)[] = Object.keys(DEFAULTS) as (keyof FormState)[];
  for (const k of keys) {
    if (prev[k] !== next[k]) {
      // Cast: heterogeneous values; the runtime shape matches the FormState
      // field type by construction.
      (out[k] as FormState[typeof k]) = next[k];
    }
  }
  return out;
}
